import React, { useState } from 'react';
import { Users, AlertTriangle, Briefcase, TrendingUp, DollarSign, CheckCircle, FlaskConical } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { useAdminData } from '@/hooks/useAdminData';
import { useDisputes } from '@/hooks/useDisputes';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';

export default function AdminDashboardPage() {
  const [showTestData, setShowTestData] = useState(false);
  const { stats, pendingVendors, verificationRequests, loading, approveVerificationRequest } = useAdminData({ includeTestData: showTestData });
  const { disputes } = useDisputes();

  if (loading) {
    return (
      <div className="p-6">
        <div className="animate-pulse space-y-6">
          <div className="h-8 bg-muted rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="h-32 bg-muted rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  const urgentDisputes = disputes.filter(d => d.status === 'open').length;
  const recentVendors = pendingVendors.slice(0, 5);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold">Admin Dashboard</h1>
          <p className="text-muted-foreground">
            Monitor platform activity and manage vendors & disputes
          </p>
        </div>
        <div className="flex items-center gap-2 px-3 py-2 border rounded-md bg-muted/30">
          <FlaskConical className="h-4 w-4 text-muted-foreground" />
          <Label htmlFor="show-test-dashboard" className="text-sm font-normal cursor-pointer">Include test data</Label>
          <Switch id="show-test-dashboard" checked={showTestData} onCheckedChange={setShowTestData} />
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Pending Vendors</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.pendingVendors || 0}</div>
            <p className="text-xs text-muted-foreground">
              Awaiting approval
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Disputes</CardTitle>
            <AlertTriangle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.activeDisputes || 0}</div>
            <p className="text-xs text-muted-foreground">
              {urgentDisputes} urgent
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Users</CardTitle>
            <Users className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalUsers || 0}</div>
            <p className="text-xs text-muted-foreground">
              Platform members
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Jobs</CardTitle>
            <Briefcase className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalJobs || 0}</div>
            <p className="text-xs text-muted-foreground">
              All time
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Bids</CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.totalBids || 0}</div>
            <p className="text-xs text-muted-foreground">
              Submitted
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Completion Rate</CardTitle>
            <CheckCircle className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats?.completionRate || 0}%</div>
            <p className="text-xs text-muted-foreground">
              Jobs completed
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Verification Requests */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Verification Requests</CardTitle>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/vendors">View All</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {verificationRequests.length > 0 ? (
              verificationRequests.slice(0, 3).map((request) => (
                <div key={request.id} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex-1">
                    <p className="font-medium">{request.vendor_profiles_enhanced?.organization?.name || request.vendor_profiles_enhanced?.business_name || 'Unnamed Business'}</p>
                    <p className="text-sm text-muted-foreground">
                      {request.requested_level} verification • {new Date(request.requested_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={() => approveVerificationRequest(request.id, true)}
                      className="bg-green-600 hover:bg-green-700 text-white"
                    >
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => approveVerificationRequest(request.id, false)}
                    >
                      Reject
                    </Button>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-sm text-muted-foreground text-center py-4">
                No pending verification requests
              </p>
            )}
          </CardContent>
        </Card>

        {/* Active Disputes */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle>Active Disputes</CardTitle>
            <Button asChild variant="outline" size="sm">
              <Link to="/admin/disputes">View All</Link>
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {disputes.slice(0, 5).map((dispute) => (
              <div key={dispute.id} className="flex items-center justify-between">
                <div className="flex-1">
                  <p className="font-medium">Dispute #{dispute.public_id}</p>
                  <p className="text-sm text-muted-foreground">
                    {new Date(dispute.created_at).toLocaleDateString()}
                  </p>
                </div>
                <Badge variant={dispute.status === 'open' ? 'destructive' : 'secondary'}>
                  {dispute.status.replace('_', ' ')}
                </Badge>
              </div>
            ))}
            {disputes.length === 0 && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No active disputes
              </p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Navigation */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Button asChild variant="outline" className="h-24 flex-col gap-2">
          <Link to="/admin/vendors">
            <Users className="h-6 w-6" />
            Vendor Management
          </Link>
        </Button>
        
        <Button asChild variant="outline" className="h-24 flex-col gap-2">
          <Link to="/admin/disputes">
            <AlertTriangle className="h-6 w-6" />
            Dispute Resolution
          </Link>
        </Button>
        
        <Button asChild variant="outline" className="h-24 flex-col gap-2">
          <Link to="/admin/audit">
            <TrendingUp className="h-6 w-6" />
            Audit Logs
          </Link>
        </Button>
      </div>
    </div>
  );
}